import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewContainerRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { SafeUrl } from '@angular/platform-browser';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, tap, throwError } from 'rxjs';

import {
  AddCandidateToFolderModalData,
  Candidate,
  CandidateActionsInfo,
  CandidateModalData,
  CandidateToFolderActionInfo,
  ConditionInfo,
} from '../../../../../../shared/models';
import {
  AccountService,
  CandidateCardHandlerService,
  ProfileService,
} from '../../../../../../shared/services';
import { SystemColors } from '../../../../../../shared/types';
import { CandidateToFoldersService, FoldersNotificationsService } from '../../../../services';
import { AddCommentModalComponent, AddTagModalComponent } from '../../../modals';
import { ActionMenuItem } from '../../../common-actions-menu';
import { TranslateService } from '@ngx-translate/core';
import { LimitationService } from '../../../../services/limitation.service';

@Component({
  selector: 'app-candidate-card-actions',
  templateUrl: './candidate-card-actions.component.html',
  styleUrls: ['./candidate-card-actions.component.scss'],
  standalone: false,
})
export class CandidateCardActionsComponent implements OnDestroy, OnInit {
  private accountService = inject(AccountService);
  private limitationService = inject(LimitationService);
  dialog = inject(MatDialog);
  private profileService = inject(ProfileService);
  private candidateToFolders = inject(CandidateToFoldersService);
  private cardHandler = inject(CandidateCardHandlerService);
  private viewContainerRef = inject(ViewContainerRef);
  private foldersNotifications = inject(FoldersNotificationsService);
  private translateService = inject(TranslateService);

  @Input() type: 'standard' | 'forFolder' = 'standard';
  @Input() candidate: Candidate;
  @Input() folderId = '';
  @Input() index = 0;
  @Input() picture: SafeUrl;
  @Input() revealed = false;

  @Output() removeFromFolder = new EventEmitter<null>();

  private isAddToFolderStarted = false;
  private updateIsAddToFoldersStartedInfo$ = new BehaviorSubject<ConditionInfo>({
    condition: this.isAddToFolderStarted,
  });
  private unsubscribe$ = new Subject<void>();

  protected isLimited = false;
  protected readonly isLimited$ = this.accountService.isLimited$();

  private lastActionsInfo: CandidateActionsInfo | null = null;
  protected actionsInfo$ = this.cardHandler.actionsInfo$.pipe(
    tap((info: CandidateActionsInfo) => {
      this.lastActionsInfo = info;
      this.setUpContextMenu(info);
    }),
  );
  protected isAddToFolderStartedInfo$: Observable<ConditionInfo> =
    this.updateIsAddToFoldersStartedInfo$.asObservable();
  protected contextMenuItems$ = new BehaviorSubject<ActionMenuItem[]>([]);
  protected contextMenuTotalCount = 0;

  ngOnInit(): void {
    this.isLimited$.pipe(takeUntil(this.unsubscribe$)).subscribe((isLimited) => {
      this.isLimited = isLimited;
      if (this.lastActionsInfo) {
        this.setUpContextMenu(this.lastActionsInfo);
      }
    });
  }

  addComment(): void {
    const data: CandidateModalData = {
      candidate: this.candidate,
      index: this.index,
    };

    this.dialog.open(AddCommentModalComponent, {
      viewContainerRef: this.viewContainerRef,
      data,
    });
  }

  addTag(): void {
    if (this.candidate?.docId) {
      const data: CandidateModalData = {
        candidate: this.candidate,
        index: this.index,
      };

      this.dialog.open(AddTagModalComponent, {
        viewContainerRef: this.viewContainerRef,
        data,
      });
    }
  }

  onFolderAction(): void {
    if (this.type === 'forFolder') {
      this.removeFromFolder.emit(null);
    } else {
      this.addCandidateToFolder();
    }
  }

  private addCandidateToFolder(): void {
    if (!this.isAddToFolderStarted) {
      this.setAddCandidateToFolderStarted(true);

      const defaultColor: SystemColors = this.profileService.getCandidateDefaultColorByIndex(
        this.index,
      );
      const data: AddCandidateToFolderModalData = {
        docId: this.candidate?.docId,
        defaultColor,
        candidate: this.candidate,
      };

      this.candidateToFolders
        .addCandidateToFolder(data)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            this.resetIsAddCandidateToFolderStarted();

            return throwError(error);
          }),
          takeUntil(this.unsubscribe$),
          finalize(() => this.resetIsAddCandidateToFolderStarted()),
        )
        .subscribe((info: CandidateToFolderActionInfo) => {
          this.updateFoldersCountByInfo(info);
          this.foldersNotifications.handleCandidateToFolderNotification(info);
        });
    }
  }

  private setAddCandidateToFolderStarted(started: boolean): void {
    this.isAddToFolderStarted = started;

    this.updateIsAddToFoldersStartedInfo$.next({ condition: started });
  }

  private setUpContextMenu(actionsInfo: CandidateActionsInfo): void {
    const { commentCount = 0, tagCount = 0 } = actionsInfo || {};
    const commentsLabel = this.translateService.instant('COMMENT.COMMENTS');
    const commentsFullLabel = commentCount ? `${commentsLabel} ${commentCount}` : commentsLabel;
    const tagsLabel = this.translateService.instant('TAGS.TAGS');
    const tagsFullLabel = tagCount ? `${tagsLabel} ${tagCount}` : tagsLabel;

    const items: ActionMenuItem[] = [
      {
        id: 'add-comment',
        iconRef: 'comment',
        label: commentsFullLabel,
        disabled: false,
        locked: this.isLimited,
      },
      {
        id: 'add-tag',
        iconRef: 'tags',
        label: tagsFullLabel,
        disabled: false,
        locked: this.isLimited,
      },
    ];

    this.contextMenuTotalCount = commentCount + tagCount;
    this.contextMenuItems$.next(items);
  }

  onProfileAddedToCampaign(): void {
    this.cardHandler.incrementCampaignCount();
  }

  onContextMenuAction(actionId: string): void {
    if (this.isLimited && (actionId === 'add-comment' || actionId === 'add-tag')) {
      this.limitationService.onPlanLimit();
      return;
    }
    if (actionId === 'add-comment') {
      this.addComment();
    } else if (actionId === 'add-tag') {
      this.addTag();
    }
  }

  private updateFoldersCountByInfo(info: CandidateToFolderActionInfo): void {
    if (typeof info?.folders?.length === 'number') {
      this.cardHandler.updateFoldersCount(info.folders.length);
    }
  }

  private resetIsAddCandidateToFolderStarted(): void {
    if (this.isAddToFolderStarted) {
      this.setAddCandidateToFolderStarted(false);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
